import React, { useEffect, useRef, useState } from 'react';
import { useProduct, useReviews, useDataModel } from '@thd-nucleus/data-sources';
import { useReviewsDeprecated, useProductDeprecated } from "@thd-nucleus/data-sources/react/dataModel/migration";
import { ReviewModel } from './models/ReviewModel';
import { ReviewStatsModel } from './models/ReviewStatsModel';

/**
 * This hook calls the review services (BV) and falls back to review data from product model if it fails.
 * @param itemId
 * @param options - RR state which contains filtering options for the BV call
 * @returns { averageRating, loading, FilterSelected, reviewModels, statistics, SortBy, totalReviews } - based on available data source
 */
export const useProductReviews = ({ itemId, ...options }) => {
  const { filters, pageContext, searchText, selectedSentiment, sortBy } = options;
  const initialOptions = useRef({});
  const [queryVariables, setQueryVariables] = useState({
    filters: {
      isVerifiedPurchase: false,
      prosCons: null,
      starRatings: null
    },
    searchTerm: null,
    sortBy: 'photoreview',
    startIndex: 1
  });

  useEffect(() => {
    if (!options) return;
    if (!initialOptions.current) {
      initialOptions.current = options;
      return;
    }
    if (JSON.stringify(options) === JSON.stringify(initialOptions.current)) return;
    initialOptions.current = options;

    const { currentPage } = pageContext;
    const verifiedFilter = filters.find(eachFilter => eachFilter.id === 'verified');
    const checkedFilters = filters.filter(eachFilter => eachFilter.id !== 'verified' && eachFilter.checked);
    const isVerifiedPurchase = verifiedFilter && verifiedFilter.checked;
    const starRatings = checkedFilters.length ? checkedFilters.map(f => Number(f.value) ): null;
    const { ReviewIds } = selectedSentiment || {};
    const reviewsPerPage = 10;
    let sentimentReviewIds = null;
    if((ReviewIds || [] ).length) {
      sentimentReviewIds = ReviewIds.join();
    }

    const reviewQueryFilters = {
      isVerifiedPurchase,
      prosCons: sentimentReviewIds,
      starRatings
    };

    let startIndex = Math.max(( currentPage - 1 ) * reviewsPerPage, 1);
    startIndex = currentPage > 1 ? startIndex + 1 : startIndex;
    const variables = {
      filters: reviewQueryFilters,
      searchTerm: searchText,
      sortBy,
      startIndex
    };
    setQueryVariables(variables);
  }, [options]);


  const reviewService = useReviewsDeprecated({
    itemId,
    variables: queryVariables
  });
  const reviewData = reviewService.data?.reviews;
  // todo: add store id
  const productModel = useProductDeprecated({
    itemId,
    domains: ['identifiers', 'info', 'reviews'], // match war query to leverage caching
    skip: !!reviewData
  });


  if (reviewService.loading || productModel.loading) {
    return { loading: true };
  }

  const productData = productModel.data?.product?.reviews;

  if (reviewData) {
    const { FilterSelected, Results, SortBy } = reviewData;
    const results = Results || [];
    const reviewModels = results.map((result) => new ReviewModel(result));
    const statistics = new ReviewStatsModel(reviewData);
    return { FilterSelected, reviewModels, statistics, SortBy };
  } else if (productData) {
    const { averageRating, totalReviews } = productData.ratingsReviews || {};
    return { averageRating, totalReviews };
  }

  return {};

};
